import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Post, PostFilters, GeneratePostRequest, CalendarEntry, ContentStats } from './types'
import { useApi } from '@/hooks/use-api'

// Query keys
export const contentKeys = {
  all: ['content'] as const,
  posts: () => [...contentKeys.all, 'posts'] as const,
  postList: (filters: PostFilters) => [...contentKeys.posts(), filters] as const,
  post: (id: string) => [...contentKeys.all, 'post', id] as const,
  calendar: () => [...contentKeys.all, 'calendar'] as const,
  stats: () => [...contentKeys.all, 'stats'] as const,
}

// Hooks

export function usePosts(filters: PostFilters = {}) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.postList(filters),
    queryFn: async (): Promise<readonly Post[]> => {
      const params = new URLSearchParams()
      if (filters.status && filters.status !== 'all') {
        params.set('status', filters.status)
      }
      if (filters.sort) {
        params.set('sort', filters.sort)
      }
      const query = params.toString() ? `?${params.toString()}` : ''
      const res = await apiFetch<readonly Post[]>(`/api/content/posts${query}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load posts')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function usePost(id: string) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.post(id),
    queryFn: async (): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`)
      if (!res.success || !res.data) throw new Error(res.error ?? `Post ${id} not found`)
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useContentCalendar() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.calendar(),
    queryFn: async (): Promise<CalendarEntry[]> => {
      const res = await apiFetch<CalendarEntry[]>('/api/content/calendar')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load content calendar')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useContentStats() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.stats(),
    queryFn: async (): Promise<ContentStats> => {
      const res = await apiFetch<ContentStats>('/api/content/stats')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load content stats')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useGeneratePost() {
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (request: GeneratePostRequest): Promise<Post> => {
      const res = await apiFetch<Post>('/api/content/generate', {
        method: 'POST',
        body: request,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to generate post')
      return res.data
    },
  })
}

export function useApprovePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: { status: 'approved' },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to approve post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
    },
  })
}

export function useUpdatePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Post> }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: updates,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to update post ${id}`)
      return res.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.post(id) })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

export function useDeletePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const res = await apiFetch<unknown>(`/api/content/posts/${id}`, {
        method: 'DELETE',
      })
      if (!res.success) throw new Error(res.error ?? `Failed to delete post ${id}`)
      return id
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
      queryClient.removeQueries({ queryKey: contentKeys.post(id) })
    },
  })
}

export function useSchedulePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, scheduledAt }: { id: string; scheduledAt: string }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: { status: 'scheduled', scheduled_for: scheduledAt },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to schedule post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

export function useSyncEngagement() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (): Promise<{ processed: number; newLeads: number }> => {
      const res = await apiFetch<{ processed: number; newLeads: number }>('/api/content/engagement')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to sync engagement')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
    },
  })
}

export function usePublishPost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId: string }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}/publish`, {
        method: 'POST',
        body: { account_id: accountId },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to publish post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}
